import React, { useState, useEffect } from 'react';
import { processImage } from '../../utils/imageProcessing';
import { getImageDimensions, estimateFileSize } from '../../utils/imageUtils';
import { Loader, Lock, Unlock, RefreshCw } from 'lucide-react';

interface DimensionResizeProps {
  originalImage: File;
  onProcess: (dataUrl: string, dimensions: { width: number; height: number }) => void;
  onProcessingChange: (processing: boolean) => void;
  outputFormat: string;
  outputQuality: number;
  initialDimensions?: { width: number; height: number } | null;
}

export function DimensionResize({
  originalImage,
  onProcess,
  onProcessingChange,
  outputFormat,
  outputQuality,
  initialDimensions,
}: DimensionResizeProps) {
  const [dimensions, setDimensions] = useState(initialDimensions || { width: 0, height: 0 });
  const [originalDimensions, setOriginalDimensions] = useState({ width: 0, height: 0 });
  const [aspectRatio, setAspectRatio] = useState(1);
  const [maintainAspect, setMaintainAspect] = useState(true);
  const [estimatedSize, setEstimatedSize] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    getImageDimensions(originalImage).then((dims) => {
      setOriginalDimensions(dims);
      const ratio = dims.width / (dims.height || 1);
      setAspectRatio(ratio);

      const targetDims = initialDimensions || dims;
      setDimensions(targetDims);

      // Auto process initial image preview
      processImage(originalImage, {
        resize: {
          width: targetDims.width,
          height: targetDims.height,
          maintainAspectRatio: true,
        },
        output: {
          format: outputFormat,
          quality: outputQuality,
        },
      }).then((dataUrl) => onProcess(dataUrl, targetDims));
    });
  }, [originalImage]);

  useEffect(() => {
    const updateEstimatedSize = async () => {
      if (dimensions.width && dimensions.height) {
        const size = await estimateFileSize(dimensions.width, dimensions.height, outputFormat, outputQuality);
        setEstimatedSize(size);
      }
    };
    updateEstimatedSize();
  }, [dimensions, outputFormat, outputQuality]);

  const handleWidthChange = (newWidth: number) => {
    if (newWidth < 0) return;
    if (maintainAspect && aspectRatio > 0) {
      setDimensions({
        width: newWidth,
        height: Math.round(newWidth / aspectRatio),
      });
    } else {
      setDimensions((prev) => ({ ...prev, width: newWidth }));
    }
  };

  const handleHeightChange = (newHeight: number) => {
    if (newHeight < 0) return;
    if (maintainAspect && aspectRatio > 0) {
      setDimensions({
        width: Math.round(newHeight * aspectRatio),
        height: newHeight,
      });
    } else {
      setDimensions((prev) => ({ ...prev, height: newHeight }));
    }
  };

  const handleReset = () => {
    setDimensions(originalDimensions);
  };

  const handleProcess = async () => {
    if (!dimensions.width || !dimensions.height) return;
    
    setProcessing(true);
    onProcessingChange(true);
    try {
      const result = await processImage(originalImage, {
        resize: {
          width: dimensions.width,
          height: dimensions.height,
          maintainAspectRatio: maintainAspect,
        },
        output: {
          format: outputFormat,
          quality: outputQuality,
        },
      });
      onProcess(result, dimensions);
    } catch (error) {
      console.error('Resize failed:', error);
    } finally {
      setProcessing(false);
      onProcessingChange(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Width</label>
          <div className="flex rounded-lg shadow-sm border border-slate-300 overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 bg-white">
            <input
              type="number"
              value={dimensions.width || ''}
              onChange={(e) => handleWidthChange(Number(e.target.value))}
              min="1"
              max={10000}
              className="block w-full px-3 py-2 text-slate-900 border-none focus:outline-none text-sm"
              placeholder="Width"
            />
            <span className="inline-flex items-center px-3 bg-slate-100 text-slate-500 text-xs font-medium border-l border-slate-200">
              px
            </span>
          </div>
        </div>

        <div>
          <div className="flex justify-between items-center mb-1">
            <label className="block text-sm font-medium text-slate-700">Height</label>
            <button
              type="button"
              onClick={() => setMaintainAspect(!maintainAspect)}
              className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 focus:outline-none"
              title={maintainAspect ? 'Aspect ratio locked' : 'Aspect ratio unlocked'}
            >
              {maintainAspect ? <Lock className="w-3.5 h-3.5 text-blue-600" /> : <Unlock className="w-3.5 h-3.5 text-slate-400" />}
              <span>{maintainAspect ? 'Locked' : 'Unlocked'}</span>
            </button>
          </div>
          <div className="flex rounded-lg shadow-sm border border-slate-300 overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 bg-white">
            <input
              type="number"
              value={dimensions.height || ''}
              onChange={(e) => handleHeightChange(Number(e.target.value))}
              min="1"
              max={10000}
              className="block w-full px-3 py-2 text-slate-900 border-none focus:outline-none text-sm"
              placeholder="Height"
            />
            <span className="inline-flex items-center px-3 bg-slate-100 text-slate-500 text-xs font-medium border-l border-slate-200">
              px
            </span>
          </div>
        </div>
      </div>

      {estimatedSize && (
        <div className="text-xs text-slate-500 flex items-center justify-between bg-slate-100/70 px-3 py-2 rounded-md">
          <span>Estimated output size: <strong className="text-slate-800">{estimatedSize}</strong></span>
          <span className="text-slate-400">Original: {originalDimensions.width}×{originalDimensions.height}px</span>
        </div>
      )}

      <div className="flex items-center justify-between pt-2">
        <button
          type="button"
          onClick={handleReset}
          className="text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1 font-medium px-2 py-1 rounded hover:bg-slate-100 transition-colors"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Reset to Original
        </button>
        <button
          type="button"
          onClick={handleProcess}
          disabled={processing || !dimensions.width || !dimensions.height}
          className="px-5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 flex items-center shadow-md hover:shadow"
        >
          {processing ? (
            <>
              <Loader className="w-4 h-4 mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            'Apply Resize'
          )}
        </button>
      </div>
    </div>
  );
}